import React, { useEffect, useState } from "react";
import {
	alpha, Box, Button, Card, CardContent, CardHeader, Checkbox, Collapse, FormControl,
	FormControlLabel, InputAdornment, InputLabel, MenuItem, OutlinedInput,
	Select, TextField, Typography, Alert, CircularProgress, useTheme, Avatar
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphoneOutlined";
import LockOpenIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckIcon from "@mui/icons-material/Check";
import ShieldIcon from "@mui/icons-material/ShieldOutlined";
import LockIcon from "@mui/icons-material/Lock";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import countries from "../SignUp/countries";
import { useNavigate } from "react-router-dom";
import { theme, buttonTheme } from "../../theme";
import { ThemeProvider } from "@mui/material/styles";
import Logo from "../../Logo_No_Bg.png";
import { config } from "../../config";
import { getAuthSession, setAuthSession } from "../../utils/authSession";
import {
	LOGIN_LOCKED_PREFIX,
	OTP_COOLDOWN_SECONDS,
	postJson,
	resolveOtpStatus,
	useCountdown,
} from "../../utils/authApi";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{10}$/;
const intlPhoneRegex = /^\+\d{7,15}$/;

const PASSWORD_SYMBOLS = "@ $ ! % * ? &";

const validatePassword = (p) =>
	/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,20}$/.test(p);

const SERVER_ERROR_MSG = "Something went wrong. Please try again.";
const LOCK_UI_DISABLE_MS = 30 * 1000;
const SEND_FAILED_MSG = "We could not send the code right now. Please try again later.";

const flagUrl = (iso) => `https://flagcdn.com/24x18/${iso.toLowerCase()}.png`;

const toJsIsoMillis = (iso) => {
	if (!iso || typeof iso !== "string") return null;
	const [date, time] = iso.split("T");
	if (!time) return null;
	const [hms, fracAndZone] = time.split(".");
	if (!fracAndZone) return iso; // already no fractional part
	// strip trailing Z if present, clamp to 3 digits, then re-append Z if it existed
	const hadZ = fracAndZone.endsWith("Z");
	const frac = (hadZ ? fracAndZone.slice(0, -1) : fracAndZone).slice(0, 3).padEnd(3, "0");
	return `${date}T${hms}.${frac}${hadZ ? "Z" : ""}`;
};
const smartNormalizeExpiry = (rawIso) => {
	if (!rawIso || typeof rawIso !== "string") return null;

	// First, reduce to milliseconds (handles long fractional seconds)
	const msIso = toJsIsoMillis(rawIso);

	// If the string already has a timezone (Z or ±hh:mm), parse directly.
	const hasTZ = /[zZ]|[+-]\d{2}:?\d{2}$/.test(msIso);

	// Try parse as-is
	let t = Date.parse(msIso);
	// If no TZ, also try as UTC by appending Z
	if (!hasTZ) {
		const tUtc = Date.parse(msIso.endsWith("Z") ? msIso : `${msIso}Z`);
		// Prefer the UTC interpretation
		if (Number.isFinite(tUtc)) t = tUtc;
	}

	if (!Number.isFinite(t)) return null;

	// Always return canonical UTC ISO string with Z
	return new Date(t).toISOString();
};

const NODE = 38;
const NODE_GAP = 6;

const STEP_COLORS = {
	doneBg: (t) => alpha(t.palette.primary.main, 0.25),
	doneFg: "#4a6200",
	activeBg: (t) => t.palette.primary.main,
	activeFg: "#111",
	activeRing: (t) => `0 0 0 4px ${alpha(t.palette.primary.main, 0.25)}`,
	idleBg: (t) => t.palette.grey[100],
	idleBorder: (t) => t.palette.grey[300],
	idleFg: (t) => t.palette.grey[500],
	lineIdle: (t) => t.palette.grey[300],
	lineDone: (t) => alpha(t.palette.primary.dark, 0.5),
};

const StepNode = ({ icon: Icon, status, label, first, last }) => {
	const t = useTheme();
	const active = status === "active";
	const done = status === "done";
	const line = {
		position: "absolute",
		top: NODE / 2 - 1,
		height: 2,
		borderRadius: 1,
	};

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				position: "relative",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
			}}
		>
			{!first && (
				<Box
					sx={{
						...line,
						left: 0,
						right: `calc(50% + ${NODE / 2 + NODE_GAP}px)`,
						bgcolor: active || done ? STEP_COLORS.lineDone(t) : STEP_COLORS.lineIdle(t),
					}}
				/>
			)}
			{!last && (
				<Box
					sx={{
						...line,
						left: `calc(50% + ${NODE / 2 + NODE_GAP}px)`,
						right: 0,
						bgcolor: done ? STEP_COLORS.lineDone(t) : STEP_COLORS.lineIdle(t),
					}}
				/>
			)}
			<Box
				sx={{
					width: NODE,
					height: NODE,
					borderRadius: "50%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontSize: 13,
					fontWeight: 700,
					mb: 0.75,
					bgcolor: done
						? STEP_COLORS.doneBg(t)
						: active
							? STEP_COLORS.activeBg(t)
							: STEP_COLORS.idleBg(t),
					color: done
						? STEP_COLORS.doneFg
						: active
							? STEP_COLORS.activeFg
							: STEP_COLORS.idleFg(t),
					border: done || active ? "none" : `1px solid ${STEP_COLORS.idleBorder(t)}`,
					boxShadow: active ? STEP_COLORS.activeRing(t) : "none",
				}}
			>
				{done ? <CheckIcon sx={{ fontSize: 20 }} /> : <Icon sx={{ fontSize: 20 }} />}
			</Box>
			<Typography
				variant="caption"
				sx={{
					fontSize: 12,
					fontWeight: active ? 700 : 500,
					color: active ? "text.primary" : "text.secondary",
				}}
			>
				{label}
			</Typography>
		</Box>
	);
};

const ctaSx = { fontWeight: 550, py: 1.1 };

const fieldSx = {
	"& .MuiInputLabel-root.Mui-focused": { color: "text.primary" },
	"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: "primary.dark",
	},
};

const RESET_COPY = [
	{ title: "Forgot your password?", sub: "Enter your email or phone and we will send you a code." },
	{ title: "Check your messages", sub: "Enter the 6-digit code we sent you." },
	{ title: "Create a new password", sub: "Choose a strong password for your account." },
];

const PASSWORD_RULES = [
	{ key: "len", txt: "8+ characters", test: (p) => p.length >= 8 },
	{ key: "up", txt: "Uppercase", test: (p) => /[A-Z]/.test(p) },
	{ key: "low", txt: "Lowercase", test: (p) => /[a-z]/.test(p) },
	{ key: "num", txt: "Number", test: (p) => /\d/.test(p) },
	{ key: "sym", txt: `Symbol (${PASSWORD_SYMBOLS})`, test: (p) => /[@$!%*?&]/.test(p) },
];

const ONBOARDING_BASE = `${config.API_MWSVENDORONBOARDING_BASE_URL}/microstores`;

const formatLocation = (d = {}) => {
	const line1 = d.addressLine1 || "";
	const line2 = d.addressLine2 || ""
	const city = d.city || "";
	const pin = d.postalCode || "";
	return [
		line1,
		line2 ? (line1 ? `, ${line2}` : line2) : "",
		city ? (line2 ? `, ${city}` : city) : "",
		pin ? (city ? ` - ${pin}` : pin) : ""
	].join("");
};

async function fetchAndStoreBusinessProfile({ vendorId, sessionToken, store }) {
	try {
		const res = await fetch(`${ONBOARDING_BASE}/vendorProfile/${vendorId}`, {
			method: "GET",
			headers: { "X-Session-Token": sessionToken },
		});
		const json = await res.json().catch(() => ({}));
		if (res.ok && json?.statusCode === 200 && json?.data) {
			const name = String(json.data.businessName || "");
			const loc = formatLocation(json.data);
			if (name) store.setItem("businessName", name);
			if (loc) store.setItem("businessLocation", loc);

			// notify the app (Header can listen once and update)
			window.dispatchEvent(new CustomEvent("vendorProfileCached", {
				detail: { businessName: name, businessLocation: loc }
			}));
		}
	} catch {
		// ignore non-blocking fetch errors
	}
}

export default function SignIn() {

	const navigate = useNavigate();
	const { isLoggedIn } = getAuthSession();
	const [isMobile, setIsMobile] = useState(false);
	useEffect(() => {
		const onR = () => setIsMobile(window.innerWidth < 600);
		onR();
		window.addEventListener("resize", onR);
		return () => window.removeEventListener("resize", onR);
	}, []);

	useEffect(() => {
		if (isLoggedIn) {
			navigate("/home", { replace: true });
		}
	}, [isLoggedIn, navigate]);

	const [mode, setMode] = useState("signin");

	const [uid, setUid] = useState("");
	const [pwd, setPwd] = useState("");
	const [rememberMe, setRememberMe] = useState(true);

	const [uidMode, setUidMode] = useState("text");
	const [country, setCountry] = useState("91");
	const [showPwd, setShowPwd] = useState(false);
	const [sidErr, setSidErr] = useState("");
	const [sidLoad, setSidLoad] = useState(false);

	const [toast, setToast] = useState({ type: "", msg: "" });
	useEffect(() => {
		if (!toast.msg) return undefined;
		const t = setTimeout(() => setToast({ type: "", msg: "" }), 6000);
		return () => clearTimeout(t);
	}, [toast]);

	const [step, setStep] = useState(0); // 0 1 2
	const [fpInput, setFpInput] = useState("");
	const [fpOtp, setFpOtp] = useState("");
	const { remaining: fpT, start: startTimer, reset: resetTimer } = useCountdown(OTP_COOLDOWN_SECONDS);
	const [fpPwd1, setFpPwd1] = useState("");
	const [fpPwd2, setFpPwd2] = useState("");
	const [fpErr, setFpErr] = useState("");
	const [fpLoad, setFpLoad] = useState(false);
	const [lockedUntil, setLockedUntil] = useState(0);
	const [now, setNow] = useState(Date.now());

	const [vendorID, setVendorID] = useState("");

	const resetFp = () => {
		setStep(0);
		setFpInput("");
		setFpOtp("");
		resetTimer();
		setFpPwd1("");
		setFpPwd2("");
		setFpErr("");
		setFpLoad(false);
	};

	useEffect(() => {
		if (lockedUntil <= Date.now()) return undefined;
		const id = setInterval(() => {
			const t = Date.now();
			setNow(t);
			if (t >= lockedUntil) clearInterval(id);
		}, 1000);
		return () => clearInterval(id);
	}, [lockedUntil]);
	const loginLocked = lockedUntil > now;
	const goBackStep = () => {
		setStep(0);
		setMode("id");
		setFpOtp("");
		setFpPwd1("");
		setFpPwd2("");
		setFpErr("");
	};
	const back = () => {
		setMode("signin");
		resetFp();
	};

	const onUid = (e) => {
		const v = e.target.value.trim();
		setUid(v);
		!v || !isNaN(Number(v)) ? setUidMode("tel") : setUidMode("text");
		if (v === "" || emailRegex.test(v) || phoneRegex.test(v)) setSidErr("");
		else setSidErr("Enter a valid email or phone");
	};
	const canLogin =
		(emailRegex.test(uid) || phoneRegex.test(uid)) && pwd.length > 0;

	const handleLogin = () => {

		if (!canLogin || loginLocked) return;
		setSidLoad(true);
		const username =
			uidMode === "tel"
				? `+${String(country)}${uid.replace(/\D/g, "")}`
				: uid;

		postJson(`${ONBOARDING_BASE}/vendorLogin`, { username, password: pwd })
			.then(async ({ status, data }) => {
				setSidLoad(false);
				if (data.authenticated) {
					let normalizedExp = smartNormalizeExpiry(data.expiresAt);
					if (!normalizedExp) {
						normalizedExp = new Date(Date.now() + 60 * 60 * 1000).toISOString(); // fallback +1h
					}

					setAuthSession({
						vendorId: data.vendorId,
						sessionToken: data.sessionToken,
						sessionStatus: data.sessionStatus,
						userName: data.userName,
						expiresAt: normalizedExp,
						rememberMe: rememberMe
					});

					await fetchAndStoreBusinessProfile({
						vendorId: data.vendorId,
						sessionToken: data.sessionToken,
						store: rememberMe ? localStorage : sessionStorage,
					});

					setToast({ type: "success", msg: "Login successful! Redirecting…" });
					setTimeout(() => navigate("/home", { replace: true }), 800);
				} else if (status === 401 && String(data.message || "").startsWith(LOGIN_LOCKED_PREFIX)) {
					setLockedUntil(Date.now() + LOCK_UI_DISABLE_MS);
					setNow(Date.now());
					setToast({ type: "error", msg: data.message });
				} else if (status >= 500 || status === 0) {
					setToast({ type: "error", msg: SERVER_ERROR_MSG });
				} else {
					setToast({ type: "error", msg: "Invalid email / phone or password" });
				}
			})
			.catch(() => {
				setSidLoad(false);
				setToast({ type: "error", msg: "Network error. Please try again." });
			});
	};

	// Phone numbers must reach the backend as +<country><digits>.
	const normalizeForgotInput = (raw) => {
		const v = raw.trim();
		if (emailRegex.test(v)) return v;
		if (intlPhoneRegex.test(v)) return v;
		if (phoneRegex.test(v)) return `+${country}${v}`;
		return null;
	};
	const fpIsNumeric = fpInput !== "" && /^\d+$/.test(fpInput);

	const sendOtp = () => {
		const forgotPasswordInput = normalizeForgotInput(fpInput);
		if (!forgotPasswordInput) {
			setFpErr("Enter valid email / phone");
			return;
		}
		setFpLoad(true);
		postJson(`${ONBOARDING_BASE}/otp/sendForgotPassword`, { forgotPasswordInput })
			.then(({ data }) => {
				setFpLoad(false);
				if (data.vendorID) {
					setVendorID(data.vendorID);
					setFpOtp("");
					setStep(1);
					setMode("otp");
					setFpErr("");
					startTimer();
				} else {
					const msg = String(data.message || "");
					const cooldown = /^Please wait/i.test(msg);
					setFpErr(cooldown ? msg : SEND_FAILED_MSG);
					if (mode === "otp" && cooldown) startTimer();
				}
			})
			.catch(() => {
				setFpLoad(false);
				setFpErr("Network error. Please try again.");
			});
	};
	const verifyOtp = () => {
		if (fpOtp.length !== 6) {
			setFpErr("Enter 6‑digit OTP");
			return;
		}
		setFpLoad(true);
		postJson(`${ONBOARDING_BASE}/otp/verifyForgotPassword`, { vendorId: vendorID, otp: fpOtp })
			.then(({ data }) => {
				setFpLoad(false);
				const code = data.optVerifyStatusCode;
				if (code === 1000) {
					setStep(2);
					setMode("pwd");
					setFpErr("");
					return;
				}
				const info = resolveOtpStatus(code);
				setFpErr(info.message);
				setFpOtp("");
				if (info.action === "restart") {
					resetTimer();
					setStep(0);
					setMode("id");
				}
			})
			.catch(() => {
				setFpLoad(false);
				setFpErr("Network error. Please try again.");
			});
	};
	const resetPwd = () => {
		if (!validatePassword(fpPwd1) || fpPwd1 !== fpPwd2) {
			setFpErr("Passwords do not match / invalid");
			return;
		}
		setFpLoad(true);
		postJson(`${ONBOARDING_BASE}/resetPasswordWithOTP`, {
			vendorId: vendorID,
			otp: fpOtp,
			newPassword: fpPwd1,
			confirmPassword: fpPwd2,
		})
			.then(({ data }) => {
				setFpLoad(false);
				if (data.resetStatus === "SUCCESS") {
					back();
					setToast({
						type: "success",
						msg: data.message || "Password reset successful. Please sign in.",
					});
				} else if (data.resetStatus === "INVALID_OTP" || data.resetStatus === "NO_VERIFIED_OTP") {
					setFpOtp("");
					setFpPwd1("");
					setFpPwd2("");
					setStep(1);
					setMode("otp");
					setFpErr(data.message || "Please verify the code again.");
				} else {
					setFpErr(data.message || "Failed to reset password. Please try again.");
				}
			})
			.catch(() => {
				setFpLoad(false);
				setFpErr("Network error. Please try again.");
			});
	};

	const stepStates = ["active", "up", "up"];
	if (step === 1) stepStates.splice(0, 2, "done", "active");
	if (step === 2) stepStates.splice(0, 3, "done", "done", "active");

	return (
		<Box
			sx={{
				minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2,
				backgroundImage: "linear-gradient(135deg,#eff6ff 0%,#eef2ff 50%,#f5f3ff 100%)",
			}}
		>
			<ThemeProvider theme={theme}>
				<Box sx={{ width: "100%", maxWidth: mode === "signin" ? 500 : 440 }}>
					<Box textAlign="center" mb={{ xs: 2, sm: 3 }}>
						<Box
							sx={{
								mx: "auto", mb: 1, p: 1,
								borderRadius: 6, bgcolor: "transparent", userSelect: 'none',
								display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: 'none'
							}}
						>
							<img
								style={{ width: isMobile ? 64 : 85, height: isMobile ? 64 : 85 }}
								src={Logo}
								alt="Microstores"
							/>
						</Box>
						<Typography
							variant="h3"
							fontWeight={700}
							gutterBottom
							sx={{
								fontSize:
									mode === "signin"
										? { xs: "2rem", sm: "3rem" }
										: { xs: "1.75rem", sm: "2.25rem" },
							}}
						>
							{mode === "signin" ? "Welcome Back" : RESET_COPY[step].title}
						</Typography>
						<Typography color="text.secondary">
							{mode === "signin"
								? "Sign in to your vendor dashboard"
								: RESET_COPY[step].sub}
						</Typography>
					</Box>

					<Card
						elevation={isMobile ? 1 : 3}
						sx={{
							borderRadius: 2,
							backgroundColor: (t) => alpha(t.palette.common.white, 0.9),
							backdropFilter: "blur(6px)",
						}}
					>
						{mode === "signin" && (
							<CardHeader
								title="Sign In"
								titleTypographyProps={{ variant: "h6", fontWeight: 700 }}
								subheader="Enter your credentials to access your account"
								sx={{ pt: 2, pb: 0, px: { xs: 2, sm: 3 } }}
							/>
						)}

						<CardContent
							sx={{
								pt: mode === "signin" ? 2 : 1.5,
								pb: 4,
								px: { xs: 2, sm: 3 },
								display: "flex",
								flexDirection: "column",
								gap: mode === "signin" ? 3 : 2.5,
							}}
						>
							{mode !== "signin" && (
								<>
									<Box>
										<Button
											variant="text"
											color="secondary"
											size="small"
											startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
											onClick={mode === "id" ? back : goBackStep}
											sx={{ fontWeight: 550, ml: -1 }}
										>
											{mode === "id" ? "Back to sign in" : "Back"}
										</Button>
									</Box>

									<Box display="flex" sx={{ mb: 0.5 }}>
										<StepNode first icon={EmailIcon} status={stepStates[0]} label="Identify" />
										<StepNode icon={ShieldIcon} status={stepStates[1]} label="Verify" />
										<StepNode last icon={LockIcon} status={stepStates[2]} label="Reset" />
									</Box>
								</>
							)}

							{mode === "signin" && (
								<>
									<Box display="flex" gap={2}>
										{uidMode === "tel" && (
											<FormControl sx={{ minWidth: 100 }}>
												<Select
													value={country}
													onChange={(e) => setCountry(e.target.value)}
													renderValue={(val) => {
														const c = countries.find((x) => x.phone === val);
														return c ? (
															<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
																<Box
																	component="img"
																	src={flagUrl(c.code)}
																	width={20}
																	loading="lazy"
																	alt=""
																/>
																+{c.phone}
															</Box>
														) : val;
													}}
													MenuProps={{
														PaperProps: { style: { maxHeight: 320, width: 260 } },
													}}
												>
													{countries.map((c) => (
														<MenuItem key={c.code} value={c.phone}>
															<ListItemIcon sx={{ minWidth: 32 }}>
																<Box
																	component="img"
																	src={flagUrl(c.code)}
																	width={20}
																	loading="lazy"
																	alt=""
																/>
															</ListItemIcon>
															<ListItemText primary={`${c.label}  (+${c.phone})`} />
														</MenuItem>
													))}
												</Select>
											</FormControl>
										)}
										<TextField
											fullWidth
											sx={fieldSx}
											label="Email or Phone"
											value={uid}
											onChange={onUid}
											error={!!sidErr}
											helperText={sidErr || " "}
											type={uidMode}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														{uidMode === "tel" ? (
															<PhoneIphoneIcon />
														) : (
															<EmailIcon />
														)}
													</InputAdornment>
												),
											}}
										/>
									</Box>

									<FormControl variant="outlined" fullWidth sx={fieldSx}>
										<InputLabel>Password</InputLabel>
										<OutlinedInput
											type={showPwd ? "text" : "password"}
											value={pwd}
											onChange={(e) => setPwd(e.target.value)}
											label="Password"
											startAdornment={
												<InputAdornment position="start">
													<LockOpenIcon />
												</InputAdornment>
											}
											endAdornment={
												<InputAdornment position="end">
													<Button
														size="small" color="secondary"
														onClick={() => setShowPwd((s) => !s)}
													>
														{showPwd ? <VisibilityOff /> : <Visibility />}
													</Button>
												</InputAdornment>
											}
										/>
									</FormControl>

									<Box
										display="flex"
										justifyContent="space-between"
										alignItems="center"
									>
										<FormControlLabel
											control={
												<Checkbox
													color='secondary'
													checked={rememberMe}
													onChange={(e) => setRememberMe(e.target.checked)}
													size="small"
												/>
											}
											label="Remember me"
										/>
										<Button
											variant="text"
											color="secondary"
											sx={{ fontWeight: 550 }}
											onClick={() => {
												setFpInput(uid);
												setMode("id");
											}}
										>
											Forgot password?
										</Button>
									</Box>

									<Button
										fullWidth
										variant="contained"
										color="primary"
										sx={ctaSx}
										disabled={!canLogin || sidLoad || loginLocked}
										onClick={handleLogin}
									>
										{sidLoad ? (<CircularProgress size={24} color="inherit" />) : ("Sign In")}
									</Button>

									<Typography textAlign="center" color="text.secondary">
										Don’t have an account?{" "}
										<Button
											sx={{ fontWeight: 550 }}
											variant="text" color="secondary" onClick={() => navigate("/signup")}>
											Create your account now
										</Button>
									</Typography>
								</>
							)}

							{mode === "id" && (
								<>
									<Box display="flex" gap={2}>
										{fpIsNumeric && (
											<FormControl sx={{ minWidth: 100 }}>
												<Select
													value={country}
													onChange={(e) => setCountry(e.target.value)}
													renderValue={(val) => `+${val}`}
													MenuProps={{
														PaperProps: { style: { maxHeight: 320, width: 260 } },
													}}
												>
													{countries.map((c) => (
														<MenuItem key={c.code} value={c.phone}>
															{`${c.label}  (+${c.phone})`}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										)}
										<TextField
											label="Email or Phone"
											fullWidth
											sx={fieldSx}
											value={fpInput}
											onChange={(e) => setFpInput(e.target.value.trim())}
											error={!!fpErr}
											helperText={fpErr || " "}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														{fpIsNumeric ? <PhoneIphoneIcon /> : <EmailIcon />}
													</InputAdornment>
												),
											}}
										/>
									</Box>

									<Button
										fullWidth
										variant="contained"
										color="primary"
										sx={ctaSx}
										onClick={sendOtp}
										disabled={fpLoad}
									>
										{fpLoad ? (
											<CircularProgress size={24} color="inherit" />
										) : (
											"Send Verification Code"
										)}
									</Button>
								</>
							)}

							{mode === "otp" && (
								<>
									<TextField
										label="Verification Code"
										fullWidth
										sx={fieldSx}
										value={fpOtp}
										onChange={(e) =>
											setFpOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
										}
										inputProps={{
											style: { textAlign: "center", letterSpacing: 4 },
											maxLength: 6,
										}}
										error={!!fpErr}
										helperText={fpErr || "Check your email / phone"}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<ShieldIcon />
												</InputAdornment>
											),
										}}
									/>

									<Button
										fullWidth onClick={verifyOtp}
										variant="contained" color="primary"
										sx={ctaSx}
										disabled={fpLoad || fpOtp.length !== 6}
									>
										{fpLoad ? (<CircularProgress size={24} color="inherit" />) : ("Verify Code")}
									</Button>

									<Box
										display="flex"
										justifyContent="center"
										alignItems="center"
										gap={0.5}
										sx={{ minHeight: 36 }}
									>
										{fpT > 0 ? (
											<Typography variant="body2" color="text.secondary">
												{`Resend in ${fpT}s`}
											</Typography>
										) : (
											<>
												<Typography variant="body2" color="text.secondary">
													Did not get it?
												</Typography>
												<Button
													variant="text"
													color="secondary"
													size="small"
													sx={{ fontWeight: 550 }}
													disabled={fpLoad}
													onClick={sendOtp}
												>
													Resend code
												</Button>
											</>
										)}
									</Box>
								</>
							)}

							{mode === "pwd" && (
								<>
									<FormControl variant="outlined" fullWidth sx={fieldSx}>
										<InputLabel>New Password</InputLabel>
										<OutlinedInput
											type="password"
											value={fpPwd1}
											onChange={(e) => setFpPwd1(e.target.value)}
											startAdornment={
												<InputAdornment position="start">
													<LockIcon />
												</InputAdornment>
											}
											label="New Password"
										/>
									</FormControl>

									<FormControl variant="outlined" fullWidth sx={fieldSx}>
										<InputLabel>Confirm Password</InputLabel>
										<OutlinedInput
											type="password"
											value={fpPwd2}
											onChange={(e) => setFpPwd2(e.target.value)}
											startAdornment={
												<InputAdornment position="start">
													<LockIcon />
												</InputAdornment>
											}
											label="Confirm Password"
										/>
									</FormControl>

									<Box
										sx={{
											display: "grid",
											gridTemplateColumns: "1fr 1fr",
											columnGap: 2,
											rowGap: 0.5,
											ml: 0.5,
										}}
									>
										{PASSWORD_RULES.map(({ key, txt, test }) => {
											const ok = test(fpPwd1);
											return (
												<Typography
													key={key}
													variant="body2"
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 0.75,
														fontSize: 13,
														color: ok ? "success.main" : "text.secondary",
													}}
												>
													{ok ? (
														<CheckIcon sx={{ fontSize: 16 }} />
													) : (
														<Box
															component="span"
															sx={{
																width: 8,
																height: 8,
																mx: "4px",
																borderRadius: "50%",
																border: "1px solid",
																borderColor: "grey.400",
															}}
														/>
													)}
													{txt}
												</Typography>
											);
										})}
									</Box>
									{/[^A-Za-z\d@$!%*?&]/.test(fpPwd1) && (
										<Typography variant="caption" color="error" sx={{ ml: 0.5 }}>
											Only letters, numbers and {PASSWORD_SYMBOLS} are allowed.
										</Typography>
									)}

									{fpErr && <Alert severity="error">{fpErr}</Alert>}

									<Button
										fullWidth
										variant="contained"
										color="primary"
										sx={ctaSx}
										onClick={resetPwd}
										disabled={fpLoad || !validatePassword(fpPwd1) || fpPwd1 !== fpPwd2}
									>
										{fpLoad ? (<CircularProgress size={24} color="inherit" />) : ("Reset Password")}
									</Button>
								</>
							)}
						</CardContent>
					</Card>

					<Collapse in={!!toast.msg}>
						{toast.msg && (
							<Alert
								severity={toast.type || "info"}
								sx={{ mt: 2 }}
								onClose={() => setToast({ type: "", msg: "" })}
								icon={toast.type === "success" ? <CheckIcon /> : undefined}
							>
								{toast.msg}
							</Alert>
						)}
					</Collapse>
				</Box>
			</ThemeProvider>
		</Box>
	);
}
